import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

interface BrandLogoProps {
  logoHeight?: number;
  titleLevel?: 1 | 2 | 3 | 4 | 5;
  showTitle?: boolean;
  showTagline?: boolean;
  style?: React.CSSProperties;
  className?: string;
  theme?: 'light' | 'dark';
  variant?: 'default' | 'sidebar' | 'header' | 'bright';
}

const BrandLogo: React.FC<BrandLogoProps> = ({
  logoHeight = 48,
  titleLevel = 2,
  showTitle = true,
  showTagline = false,
  style,
  className,

  variant = 'default'
}) => {
  const { t } = useTranslation(['auth']);

  const getContainerStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...style
    };

    if (variant === 'sidebar') {
      return {
        ...baseStyle,
        height: '64px',
        margin: '0px',
        background: '#001529',
        flexShrink: 0,
        padding: '0 16px'
      };
    }

    if (variant === 'header') {
      return {
        ...baseStyle,
        height: '100%',
        padding: '0 16px'
      };
    }

    if (variant === 'bright') {
      return {
        ...baseStyle,
        height: '64px',
        margin: '0px',
        flexShrink: 0,
        padding: '0 16px'
      };
    }

    return baseStyle;
  };

  const getTextContainerStyle = (): React.CSSProperties => {
    if (variant === 'sidebar' || variant === 'header' || variant === 'bright') {
      return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start'
      };
    }
    return {};
  };

  const getTitleStyle = (): React.CSSProperties => {
    if (variant === 'sidebar') {
      return {
        margin: 0,
        color: 'white',
        fontSize: 18, // Updated to 18px as requested
        fontWeight: 600, // Updated to 600 as requested
        letterSpacing: 0, // Changed from 1 to 0 (normal)
        fontFamily: 'Outfit, sans-serif' // Added Outfit font for brand text
      };
    }

    if (variant === 'bright') {
      return {
        margin: 0,
        color: '#001529', // Dark blue for bright backgrounds
        fontSize: 18,
        fontWeight: 600,
        letterSpacing: 0,
        fontFamily: 'Outfit, sans-serif'
      };
    }

    return {
      margin: 0,
      fontFamily: 'Outfit, sans-serif' // Added Outfit font for brand text
    };
  };

  const getTaglineStyle = (): React.CSSProperties => {
    if (variant === 'sidebar') {
      return {
        margin: 0,
        marginTop: -2,
        color: 'rgba(255, 255, 255, 0.65)', // Semi-transparent white for sidebar
        fontSize: 10,
        fontWeight: 300,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        fontFamily: 'Poppins, Arial, sans-serif'
      };
    }

    if (variant === 'header') {
      return {
        margin: 0,
        marginTop: -2,
        color: 'rgba(0, 0, 0, 0.65)', // Semi-transparent black for header
        fontSize: 10,
        fontWeight: 300,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        fontFamily: 'Poppins, Arial, sans-serif'
      };
    }

    if (variant === 'bright') {
      return {
        margin: 0,
        marginTop: -2,
        color: 'rgba(0, 0, 0, 0.65)', // Semi-transparent black for bright backgrounds
        fontSize: 10,
        fontWeight: 300,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        fontFamily: 'Poppins, Arial, sans-serif'
      };
    }

    return {
      margin: 0,
      marginTop: 4,
      color: 'rgba(0, 0, 0, 0.65)',
      fontSize: 10,
      fontWeight: 300,
      letterSpacing: 0.5,
      textTransform: 'uppercase' as const,
      fontFamily: 'Poppins, Arial, sans-serif'
    };
  };

  const getLogoStyle = (): React.CSSProperties => {
    if (variant === 'sidebar') {
      return {
        height: logoHeight,
        objectFit: 'contain',
        marginRight: 8
      };
    }

    return {
      height: logoHeight,
      marginRight: 12
    };
  };

  return (
    <div 
      style={getContainerStyle()}
      className={className}
    >
      <img 
        src="/logo-new.png" 
        alt="Logo" 
        style={getLogoStyle()}
      />
      {(showTitle || showTagline) && (
        <div style={getTextContainerStyle()}>
          {showTitle && (
            <Title level={titleLevel} style={getTitleStyle()}>
              {t('auth:appTitle')}
            </Title>
          )}
          {showTagline && (
            <div style={getTaglineStyle()}>
              {t('auth:tagline')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BrandLogo;
